import React from 'react';
import classNames from 'classnames';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBox, faShieldHalved, faTruck } from '@fortawesome/free-solid-svg-icons';
import { getContentTypeRule } from '../../contentTypeRules';
import styles from '../Select/Select.module.scss';

/**
 * Rich hover-preview panel content for the Content Type dropdown.
 * Renders heading, description, and detail rows for Packaging, Insurance, Carriers.
 */
export function ContentTypePreview({ value }: { value: string }): React.ReactElement {
  const rule = getContentTypeRule(value);

  if (!rule) {
    return (
      <div className={styles.previewEmpty}>
        Hover over a content type to see details
      </div>
    );
  }

  const description = rule.messages.join(' ');
  const packagingText = rule.messages[0] || 'Standard packaging requirements apply.';
  const insuranceText = rule.insuranceAllowed
    ? 'Insurance is available for this content type.'
    : 'Insurance is not available for this content type.';

  let carriersText: string;
  if (!rule.carrierFilter) {
    carriersText = 'All carriers available.';
  } else if (rule.carrierFilter.mode === 'include') {
    carriersText = rule.carrierFilter.carriers.join(', ');
  } else {
    carriersText = `All carriers except: ${rule.carrierFilter.carriers.join(', ')}`;
  }

  return (
    <div>
      <h4 className={styles.previewHeading}>{rule.label}</h4>
      <p className={styles.previewDescription}>{description}</p>

      {/* Packaging */}
      <div className={styles.previewDetailRow}>
        <span className={classNames(styles.previewDetailIcon, styles.previewDetailIconPackaging)}>
          <FontAwesomeIcon icon={faBox} />
        </span>
        <div className={styles.previewDetailContent}>
          <div className={styles.previewDetailLabel}>Packaging</div>
          <div className={styles.previewDetailValue}>{packagingText}</div>
        </div>
      </div>

      {/* Insurance */}
      <div
        className={classNames(styles.previewDetailRow, {
          [styles.previewDetailDanger]: !rule.insuranceAllowed,
        })}
      >
        <span className={classNames(styles.previewDetailIcon, styles.previewDetailIconInsurance, {
          [styles.previewDetailIconDanger]: !rule.insuranceAllowed,
        })}>
          <FontAwesomeIcon icon={faShieldHalved} />
        </span>
        <div className={styles.previewDetailContent}>
          <div className={styles.previewDetailLabel}>Insurance</div>
          <div className={styles.previewDetailValue}>{insuranceText}</div>
        </div>
      </div>

      {/* Carriers */}
      <div className={styles.previewDetailRow}>
        <span className={classNames(styles.previewDetailIcon, styles.previewDetailIconCarriers)}>
          <FontAwesomeIcon icon={faTruck} />
        </span>
        <div className={styles.previewDetailContent}>
          <div className={styles.previewDetailLabel}>Carriers</div>
          <div className={styles.previewDetailValue}>{carriersText}</div>
        </div>
      </div>
    </div>
  );
}

/**
 * Render function suitable for the Select component's richPreview prop.
 * Usage: <Select richPreview={renderContentTypePreview} ... />
 */
export function renderContentTypePreview(value: string): React.ReactNode {
  return <ContentTypePreview value={value} />;
}
